import type { KeyboardEvent } from 'react';
import { CountBadge } from './CountBadge';
import { tabButtonId, tabPanelId } from './tabIds';
import { tabTriggerClass } from './tabTriggerClass';

export type TabDescriptor<TabId extends string> = {
  id: TabId;
  label: string;
  count?: number;
  testId?: string;
};

export type TabListProps<TabId extends string> = Readonly<{
  baseId: string;
  tabs: readonly TabDescriptor<TabId>[];
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  ariaLabel: string;
}>;

/**
 * An ARIA tablist with automatic activation: arrow keys and Home/End both move
 * focus and select, so the panel always matches the focused tab.
 *
 * @example
 * <TabList
 *   baseId={useId()}
 *   tabs={[{ id: 'pending', label: 'Pending', count: 3, testId: 'tab-pending' }]}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   ariaLabel="My quotes"
 * />
 */
export function TabList<TabId extends string>({
  baseId,
  tabs,
  activeTab,
  onTabChange,
  ariaLabel,
}: TabListProps<TabId>) {
  const focusTab = (tab: TabId) => {
    onTabChange(tab);
    document.getElementById(tabButtonId(baseId, tab))?.focus();
  };

  const handleTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (tabs.length === 0) return;

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      focusTab(event.key === 'Home' ? tabs[0].id : tabs[tabs.length - 1].id);
      return;
    }

    const step =
      event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
    focusTab(tabs[(currentIndex + step + tabs.length) % tabs.length].id);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="flex items-center gap-1"
      onKeyDown={handleTabKeyDown}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          id={tabButtonId(baseId, tab.id)}
          type="button"
          role="tab"
          aria-selected={activeTab === tab.id}
          aria-controls={tabPanelId(baseId)}
          tabIndex={activeTab === tab.id ? 0 : -1}
          data-testid={tab.testId}
          className={tabTriggerClass(activeTab === tab.id)}
          onClick={() => onTabChange(tab.id)}
        >
          {tab.label}
          {tab.count !== undefined && <CountBadge count={tab.count} />}
        </button>
      ))}
    </div>
  );
}
